import {ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {
    RenderableDocument,
    DibJsonNode,
    ElementDataReferences,
    ReferenceFlags,
    ReferenceFlagsTo,
} from "../../types";
import {getSectionHeading} from "../../contentUtils";
import {usePanel} from "../../panel/PanelContext";
import PaperclipIcon from "../../../../assets/paperclip.svg?react";
import WorldIcon from "../../../../assets/world.svg?react";
import ArrowsExchangeIcon from "../../../../assets/arrows-exchange.svg?react";
import Message2Icon from "../../../../assets/message-2.svg?react";
import classes from "./sectionIconLinks.module.css";

type SectionIconLinksProps = {
    node: DibJsonNode;
    topicData: RenderableDocument;
    renderedChildren: ReactNode[];
};

const referenceSvgs: ReferenceFlagsTo<React.FC<React.SVGProps<SVGSVGElement>>> =
    {
        reference: PaperclipIcon,
        comment: Message2Icon,
        translation: WorldIcon,
        mirror: ArrowsExchangeIcon,
    };

const referenceI18nKeys: ReferenceFlagsTo<string> = {
    reference: "content.relatedSources",
    comment: "content.comment",
    translation: "content.translation",
    mirror: "content.lawRulesMirror",
};

export const SectionIconLinks = ({
    node,
    topicData,
    renderedChildren,
}: SectionIconLinksProps) => {
    const {openPanel} = usePanel();

    const {t} = useTranslation();
    const elementId = node.element?.attributes?.id ?? "";
    const sectionHeading = getSectionHeading(
        topicData.elements,
        node.element?.children
    );
    const references =
        (node.elementData as ElementDataReferences | null)?.references ?? 0;

    const resourceId = topicData.id;

    const buttons = (
        Object.keys(ReferenceFlags) as Array<keyof typeof ReferenceFlags>
    ).reduce<ReactNode[]>((acc, key) => {
        const flag = ReferenceFlags[key];
        if (!(references & flag)) return acc;

        const SvgIcon = referenceSvgs[key];
        const label = t(referenceI18nKeys[key]) || key;

        const handleClick = () => {
            if (flag > 1) {
                openPanel({
                    label,
                    fetchType: "documentParts",
                    params: {
                        resourceId,
                        segmentId: `related/${flag}`,
                        id: elementId,
                    },
                });
            } else {
                openPanel({
                    label: sectionHeading || label,
                    fetchType: "inlineRelations",
                    params: {resourceId, id: elementId},
                });
            }
        };

        acc.push(
            <div className={classes.buttonContainer} key={key}>
                <button
                    type="button"
                    className={classes.iconButton}
                    onClick={handleClick}
                    aria-label={label}
                >
                    <SvgIcon />
                    <span>{label}</span>
                </button>
            </div>
        );
        return acc;
    }, []);

    if (node.kind === "level") {
        // API data can be dirty: level nodes sometimes begin with one or more
        // whitespace-only text children ({"text": " "}) before the actual heading.
        // We skip those to find the first meaningful child to use as the header.
        //
        // TODO: Fix upstream so the renderer strips leading whitespace-only text
        // nodes before passing renderedChildren to augmentations.
        //
        // Fallback: if somehow all children are blank, Math.max(0, -1) = 0
        // preserves the original behaviour.
        const isBlankString = (child: ReactNode) =>
            typeof child === "string" && child.trim() === "";
        const headingIdx = Math.max(
            0,
            renderedChildren.findIndex((child) => !isBlankString(child))
        );
        return (
            <>
                <header className={classes.sectionHeader}>
                    {renderedChildren[headingIdx]}
                    <div className={classes.headerOptions}>{buttons}</div>
                </header>
                {renderedChildren.slice(headingIdx + 1)}
            </>
        );
    }

    return (
        <div className={classes.augmentedContent}>
            <div className={classes.contentContainer}>{renderedChildren}</div>
            <div className={classes.headerOptions}>{buttons}</div>
        </div>
    );
};
